/**
 * ScriptListItem
 *
 * Renders a single script entry in the Scripts panel — showing filename,
 * relative time, optional connection info, active indicator, hover pin toggle,
 * and a right-click context menu.
 */

import { useCallback } from 'react';
import { BookOpen, FileCode2, Pin, PinOff } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { formatRelativeTime } from '../../workspace/start-page/utils/timestampFormatting';
import { ScriptContextMenu } from './ScriptContextMenu';
import type { ScriptEntry } from '../types/scriptBrowserTypes';
import { safeServerText } from '../../../types/safeConnectionDisplay';

type ScriptListItemProps = {
  script: ScriptEntry;
  isPinned?: boolean;
  isActive: boolean;
  isFocused?: boolean;
  onOpen: (script: ScriptEntry) => void;
  onTogglePin?: (script: ScriptEntry) => void;
  /** Id of the currently active connection, or null/undefined when nothing
   *  is connected. Preferred over `activeConnectionName` for the
   *  suppression decision below — display names are not unique across
   *  saved connections (no alias-uniqueness validation exists), so two
   *  connections can share a name while being genuinely different. */
  activeConnectionId?: string | null;
  /** Display name of the currently active connection. Used only as a
   *  fallback when either side lacks a `connectionId` (e.g. a script saved
   *  before `connectionId` was tracked). */
  activeConnectionName?: string | null;
};

export function ScriptListItem({
  script,
  isPinned,
  isActive,
  isFocused,
  onOpen,
  onTogglePin,
  activeConnectionId,
  activeConnectionName,
}: ScriptListItemProps) {
  const handleClick = useCallback(() => {
    onOpen(script);
  }, [script, onOpen]);

  const handlePinClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onTogglePin?.(script);
    },
    [script, onTogglePin]
  );

  const relativeTime = formatRelativeTime(new Date(script.lastOpenedAt));
  const isNotebook = script.fileType === 'ipynb';
  const Icon = isPinned ? Pin : isNotebook ? BookOpen : FileCode2;
  // Suppress the sublabel only when it exactly repeats the connection the
  // user is already on. Compared by connection *id*, not display name —
  // display names are not guaranteed unique across saved connections, so a
  // name match alone can't tell "same connection" from "different
  // connection that happens to share a name." Only fall back to a name
  // comparison when either side lacks an id (a script saved before
  // `connectionId` was tracked). With no active connection there's nothing
  // to compare against, so the sublabel always shows in that case (PP-29).
  const showConnectionSublabel =
    !!script.connectionName &&
    (script.connectionId && activeConnectionId
      ? script.connectionId !== activeConnectionId
      : script.connectionName !== activeConnectionName);

  return (
    <ScriptContextMenu script={script} isPinned={isPinned}>
      <button
        type="button"
        className={cn(
          'group w-full flex items-start gap-2 px-2 py-1.5 text-left text-sm rounded-sm',
          'hover:bg-accent/50 transition-colors cursor-pointer',
          isActive && 'border-l-2 border-primary bg-accent/30',
          isFocused && 'ring-1 ring-ring'
        )}
        onClick={handleClick}
        data-testid="script-list-item"
        title={script.filePath}
      >
        <Icon className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-1">
            <span className="truncate font-medium">{script.fileName}</span>
            <div className="flex items-center gap-1">
              {/* Hover pin/unpin toggle */}
              {onTogglePin && (
                <span
                  role="button"
                  tabIndex={-1}
                  className={cn(
                    'opacity-0 group-hover:opacity-100 transition-opacity',
                    'p-0.5 rounded-sm hover:bg-accent cursor-pointer shrink-0'
                  )}
                  onClick={handlePinClick}
                  title={isPinned ? 'Unpin' : 'Pin'}
                  data-testid="pin-toggle"
                >
                  {isPinned ? (
                    <PinOff className="h-3.5 w-3.5 text-muted-foreground" />
                  ) : (
                    <Pin className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </span>
              )}
              <span
                className="text-[11px] text-muted-foreground whitespace-nowrap tabular-nums"
                data-testid="script-timestamp"
              >
                {relativeTime}
              </span>
            </div>
          </div>
          {showConnectionSublabel && (
            <span
              className="text-xs text-muted-foreground truncate block"
              data-testid="script-connection-sublabel"
            >
              {safeServerText(script.connectionName)}
            </span>
          )}
          {script.preview && (
            <span className="text-xs text-muted-foreground truncate block" data-testid="script-preview">
              {script.preview}
            </span>
          )}
        </div>
      </button>
    </ScriptContextMenu>
  );
}
